/* Claude 4.38 — TWO-STEP BASKET DISCOUNT: BASKET5 (5% from £350) then
 * BASKET10 (10% from £700).
 *
 * WHY A SEPARATE FILE. assets/claude-cart-drawer.js is ~51KB and
 * themeFilesUpsert rewrites a file whole, so this patches the drawer's
 * prototype instead of retyping it. It replaces exactly two methods:
 *
 *   maybeAutoApply()  — now picks between THREE codes, not two
 *   removeDiscount()  — a BASKET5 removed by hand is remembered like BASKET10
 *
 * IF claude-cart-drawer.js IS EVER REWRITTEN, fold this in and delete both
 * this file and its <script> tag in sections/claude-cart-drawer.liquid, or
 * the drawer will run two different versions of maybeAutoApply.
 *
 * WHAT CHANGED IN THE LOGIC, AND WHY:
 *
 * 1. THE MEASURED TOTAL EXCLUDES PACKAGING. The old code compared the whole
 *    pre-discount basket (gift box, protective box, tracked delivery
 *    included) against the minimum. Those three are gift-card-type products
 *    Shopify never discounts, so they never count toward a code's minimum
 *    either — which is why the live theme had the BASKET10 minimum set to
 *    £732 while the code itself says £700. This measures real products only,
 *    the same sum the Liquid bar already uses (addon_total), so the bar and
 *    the auto-apply agree and the setting can match the code: £700.
 *    Still PRE-discount (original_line_price) — see note 2 in the main file.
 *
 * 2. PRECEDENCE, one code per checkout:
 *      real products >= BASKET10 minimum  -> BASKET10
 *      else a qualifying promo (SHEEP15)  -> the promo (15% of a double
 *                                            sheepskin beats 5% of any basket
 *                                            under £650, which caps at £32.50)
 *      else real products >= £350         -> BASKET5
 *    A code of ours that is not the target is actively removed, as before.
 *
 * 3. A REJECTED CODE FALLS BACK A STEP. If Shopify refuses BASKET10 for a
 *    given basket, that basket state is remembered and BASKET5 is tried
 *    instead, rather than the shopper getting nothing. Rejections are kept
 *    per code+state so the fallback cannot loop.
 */
(() => {
  const DISMISS_KEY = 'ccd-autoapply-dismissed'; // same key the main file uses
  const UPDATE_URL = (window.routes && window.routes.cart_update_url) || '/cart/update';
  const ADDON_FALLBACK = ['luxury-gift-box', 'cardboard-package', 'tracked-shipping'];

  const dismissedList = () => {
    try {
      return String(sessionStorage.getItem(DISMISS_KEY) || '')
        .toLowerCase().split(',').map((s) => s.trim()).filter(Boolean);
    } catch (e) { return []; }
  };
  const dismiss = (code) => {
    const list = dismissedList();
    const c = String(code).toLowerCase();
    if (list.indexOf(c) === -1) list.push(c);
    try { sessionStorage.setItem(DISMISS_KEY, list.join(',')); } catch (e) { /* noop */ }
  };

  const patch = (Drawer) => {
    const proto = Drawer.prototype;
    if (proto.__ccdTiers) return false;
    proto.__ccdTiers = true;

    proto.maybeAutoApply = async function () {
      if (this._autoBusy) return;
      const inner = this.querySelector('#ClaudeCartDrawer-Inner');
      if (!inner) return;
      if (inner.dataset.itemCount === '0') return; // empty basket — no request

      const baseCode = inner.dataset.autoapplyCode || '';
      const baseMin = parseInt(inner.dataset.autoapplyMin || '0', 10);
      const lowCode = inner.dataset.autoapplyLowCode || '';
      const lowMin = parseInt(inner.dataset.autoapplyLowMin || '0', 10);
      const promoCode = inner.dataset.promoCode || '';
      const promoMax = parseInt(inner.dataset.promoMax || '0', 10);

      const managed = [baseCode, lowCode, promoCode].filter(Boolean).map((c) => c.toLowerCase());
      if (!managed.length) return;
      const isOurs = (c) => managed.indexOf(String(c).toLowerCase()) > -1;

      const handles = (typeof this.addonHandles === 'function' ? this.addonHandles() : ADDON_FALLBACK);

      this._autoBusy = true;
      let again = false; // a drop re-renders while we are busy — run once more after
      try {
        const cart = await this.fetchCart();
        if (!cart) return;

        const entries = cart.discount_codes || [];
        const codes = entries.map((d) => d.code);
        const gross = cart.original_total_price;
        const real = (cart.items || [])
          .filter((i) => handles.indexOf(i.handle) === -1)
          .reduce((sum, i) => sum + (i.original_line_price || 0), 0);
        const stateKey = `${cart.item_count}:${gross}`;

        if (!this._tierRejected) this._tierRejected = {};
        const wasRejected = (code) => !!this._tierRejected[`${stateKey}:${String(code).toLowerCase()}`];

        let target = '';
        if (baseCode && baseMin && real >= baseMin && !wasRejected(baseCode)) target = baseCode;
        else if (promoCode && (!promoMax || real < promoMax) && !wasRejected(promoCode)) target = promoCode;
        else if (lowCode && lowMin && real >= lowMin && !wasRejected(lowCode)) target = lowCode;
        const targetLower = target.toLowerCase();

        const mine = entries.filter((d) => isOurs(d.code));
        const theirs = entries.filter((d) => !isOurs(d.code));

        const drop = (codeLower) =>
          this.cartRequest(UPDATE_URL, {
            discount: codes.filter((c) => String(c).toLowerCase() !== codeLower).join(','),
          });

        const rejected = mine.find((d) => d.applicable === false);
        if (rejected) {
          this._tierRejected[`${stateKey}:${String(rejected.code).toLowerCase()}`] = true;
          await drop(String(rejected.code).toLowerCase());
          again = true; // fall back a step
          return;
        }

        const wrong = mine.find((d) => String(d.code).toLowerCase() !== targetLower);
        if (wrong) { await drop(String(wrong.code).toLowerCase()); again = true; return; }

        if (!target) return;
        if (mine.length) return;   // already on the basket
        if (theirs.length) return; // never overwrite a code the customer typed
        if (dismissedList().indexOf(targetLower) > -1) return;

        await this.cartRequest(UPDATE_URL, { discount: codes.concat(target).join(',') });
      } finally {
        this._autoBusy = false;
        /* The drop's own cartRequest -> swapSections -> maybeAutoApply ran
           while _autoBusy was still true and returned early, so without
           this a basket moving from BASKET5 to BASKET10 lost the 5% and
           never gained the 10%. Found in the 4.38 preview test. Each pass
           removes a code or adds one, so this settles in at most two. */
        if (again) setTimeout(() => this.maybeAutoApply(), 0);
      }
    };

    const origRemove = proto.removeDiscount;
    proto.removeDiscount = async function (code) {
      const inner = this.querySelector('#ClaudeCartDrawer-Inner');
      const low = inner && inner.dataset.autoapplyLowCode;
      if (low && String(code).toLowerCase() === String(low).toLowerCase()) dismiss(code);
      return origRemove.call(this, code);
    };
    return true;
  };

  if (!window.customElements) return;
  customElements.whenDefined('cart-drawer').then(() => {
    if (!patch(customElements.get('cart-drawer'))) return;
    /* The drawer ran the OLD maybeAutoApply once from its constructor before
       this file loaded. Run the new one now; if the old call is still in
       flight (_autoBusy), try again shortly. */
    const el = document.getElementById('ClaudeCartDrawer');
    if (!el) return;
    el.maybeAutoApply();
    setTimeout(() => el.maybeAutoApply(), 1500);
  });
})();
